import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Autocomplete, Stack, FormControl, Select, MenuItem, FormHelperText } from '@mui/material';
import { supabase } from '../../supabaseClient.js';
import useAlert from '../../alert/useAlert.js';
import countries from '../../constants/countries.json';
import { MENTAL_HEALTH_SPECIALITIES } from '../../constants/profileOptions.js';
import OnboardingLayout from './OnboardingLayout.js';
import ProfileAutofill from '../../components/ProfileAutofill';
import { loadProfileOptions } from '../../services/profileOptionsService';
import { buildPrefillOptionLists, isBlankValue } from '../../utils/profilePrefill.js';

const COUNTRY_NAMES = countries.map((c) => c.name);
const DEFAULT_COUNTRY = 'Hong Kong SAR';

// Auto-fill suggestions for fields that live on later steps (mostly step 5,
// "Public profile"): suggestion key -> label, user_accounts column, encrypted?
// General professional fields also fill step 4. Registry credentials remain in
// its dedicated verification form, with no direct writes from step 2.
export const LATER_STEP_FIELDS = [
  { key: 'practice_title', label: 'Practice Title', column: 'practice_title', encrypted: true, section: 'Professional qualifications (step 4)' },
  { key: 'organisation', label: 'Organization', column: 'organisation', encrypted: true, section: 'Professional qualifications (step 4)' },
  { key: 'university', label: 'University', column: 'university', section: 'Professional qualifications (step 4)' },
  { key: 'professional_qualification', label: 'Professional Qualification', column: 'professional_qualification', section: 'Professional qualifications (step 4)' },
  { key: 'experience', label: 'Experience (years)', column: 'experience', section: 'Professional qualifications (step 4)' },
  { key: 'about', label: 'About me', column: 'about', encrypted: true },
  { key: 'phone', label: 'Phone number', column: 'phone_number', encrypted: true },
  { key: 'address', label: 'Practice address', column: 'address', encrypted: true },
  { key: 'contact_email', label: 'Contact email', column: 'secondary_email', encrypted: true },
  { key: 'website', label: 'Website', column: 'website', encrypted: true },
  { key: 'languages', label: 'Languages', column: 'languages' },
  { key: 'preferred_case_types', label: 'Speciality Areas', column: 'preferred_case_types' },
  { key: 'assessment_types', label: 'Assessment Types', column: 'assessment_types' },
  { key: 'client_groups', label: 'Client Group', column: 'client_groups' },
  { key: 'intervention_approaches', label: 'Intervention Approach', column: 'intervention_approach' },
  { key: 'service_formats', label: 'Service Format', column: 'service_formats' },
];
const LATER_STEP_SECTION = 'Public profile (step 5)';

const Step2AboutYou = ({ ctx, onContinue, onStepClick }) => {
  const { userId, initials, displayName, setDisplayName } = ctx;
  const { setAlert } = useAlert();
  const [fullName, setFullName] = useState(displayName || '');
  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  // Only the create-org panel in step 1 sets practicetype; members joining an
  // existing organization set their Type/Speciality here instead.
  const [practiceType, setPracticeType] = useState('');
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const loadAccount = async () => {
      const { data } = await supabase
        .from('user_accounts')
        .select('country, practicetype')
        .eq('id', userId)
        .maybeSingle();
      if (data?.country) setCountry(data.country);
      if (data?.practicetype) setPracticeType(data.practicetype);
    };
    loadAccount();
  }, [userId]);

  // ---- Auto-fill from a web page ----
  // Saved values of the later-step fields, so AI only suggests the empty ones
  // (they're usually all empty this early, but the user may have come back).
  const [laterCurrent, setLaterCurrent] = useState(null); // null until loaded
  const [laterPending, setLaterPending] = useState({});
  const [optionsByType, setOptionsByType] = useState({});

  useEffect(() => {
    const loadAutofillContext = async () => {
      const columns = LATER_STEP_FIELDS.map((f) => f.column).join(', ');
      const { data } = await supabase.from('user_accounts').select(columns).eq('id', userId).maybeSingle();
      if (!data) return;
      const values = await Promise.all(LATER_STEP_FIELDS.map(async (f) => {
        if (!f.encrypted) return [f.key, data[f.column] ?? ''];
        if (!data[f.column]) return [f.key, ''];
        const { data: plain, error } = await supabase.rpc('decrypt_text', { encrypted_data: data[f.column] });
        // A failed decrypt is unknown, not an empty field that AI may replace.
        return [f.key, error || plain == null ? data[f.column] : plain];
      }));
      setLaterCurrent(Object.fromEntries(values));
    };
    const loadOptions = async () => {
      try {
        setOptionsByType(await loadProfileOptions(userId));
      } catch (error) {
        // Without the catalog the AI just can't pick those lists; the rest still works.
        console.error('Could not load profile options:', error);
      }
    };
    loadAutofillContext();
    loadOptions();
  }, [userId]);

  const autofillFields = [
    { key: 'name', label: 'Full name', section: 'This step', current: fullName },
    { key: 'practice_type', label: 'Type / Speciality', section: 'This step', current: practiceType },
    // Until the saved values have loaded we can't tell which are empty, so offer none.
    ...(laterCurrent === null ? [] : LATER_STEP_FIELDS).map((f) => ({
      key: f.key, label: f.label, section: f.section || LATER_STEP_SECTION, current: laterPending[f.key] ?? laterCurrent[f.key],
    })),
  ];

  const applyAutofill = (rows) => {
    const pending = {};
    const accepted = rows.filter(row => {
      const field = autofillFields.find(item => item.key === row.key);
      return field && isBlankValue(field.current);
    });
    accepted.forEach((row) => {
      if (row.key === 'name') setFullName(row.value);
      else if (row.key === 'practice_type') setPracticeType(row.value);
      else pending[row.key] = row.value;
    });
    setLaterPending((prev) => ({ ...prev, ...pending }));
    return accepted;
  };

  // Undo clears only fields still holding the AI value, so later hand edits stay.
  const undoAutofill = (rows) => {
    const keys = rows.map((row) => row.key);
    rows.forEach((row) => {
      if (row.key === 'name' && fullName === row.value) setFullName('');
      if (row.key === 'practice_type' && practiceType === row.value) setPracticeType('');
    });
    setLaterPending((prev) => Object.fromEntries(Object.entries(prev).filter(([key]) => !keys.includes(key))));
  };

  // Runs after this step's own save succeeds. A failure here mustn't block
  // onboarding: the user can still fill these fields in step 5.
  const saveAutofillForLaterSteps = async () => {
    const payload = {};
    LATER_STEP_FIELDS.forEach((f) => {
      if (laterPending[f.key] !== undefined) payload[f.column] = laterPending[f.key];
    });
    if (Object.keys(payload).length === 0) return;
    const { error } = await supabase.from('user_accounts').update(payload).eq('id', userId);
    if (error) {
      console.error('Could not save auto-filled profile fields:', error);
      setAlert('Your details were saved, but the auto-filled profile details could not be. You can add them in the later steps.', 'warning');
      return;
    }
    setLaterPending({});
  };

  const handleContinue = async () => {
    setSubmitted(true);
    if (!fullName.trim()) {
      setAlert('Please enter your full name.', 'error');
      return;
    }
    if (!practiceType) {
      setAlert('Please select your type/speciality.', 'error');
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from('user_accounts')
      .update({ name: fullName.trim(), country: country || DEFAULT_COUNTRY, practicetype: practiceType, mode: 'Mental Health' })
      .eq('id', userId);
    setSaving(false);
    if (error) {
      setAlert('Unable to save your details. Please try again.', 'error');
      return;
    }
    setDisplayName(fullName.trim());
    await saveAutofillForLaterSteps();
    onContinue();
  };

  return (
    <OnboardingLayout
      step={2}
      title="Tell us about yourself"
      description="This helps us personalize your TikCare experience and connect you with the right opportunities."
      avatarInitials={initials}
      onStepClick={onStepClick}
      onContinue={handleContinue}
      saving={saving}
    >
      <Box sx={{ maxWidth: 720, mx: 'auto' }}>
        <Box sx={{ mb: 4 }}>
          <ProfileAutofill
            fields={autofillFields}
            optionLists={buildPrefillOptionLists(optionsByType)}
            onApply={applyAutofill}
            onUndo={undoAutofill}
            saveLabel="Continue"
            appliedNote="Details for later steps are saved too, and you can edit them in steps 4 and 5."
          />
        </Box>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Full name *</Typography>
        <TextField
          fullWidth
          placeholder="Enter your full name"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          error={submitted && !fullName.trim()}
          helperText={submitted && !fullName.trim() ? 'Full name is required' : ''}
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mt: 4 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Country / Region *</Typography>
            <Autocomplete
              options={COUNTRY_NAMES}
              value={country}
              onChange={(_, value) => setCountry(value || DEFAULT_COUNTRY)}
              disableClearable
              renderInput={(params) => <TextField {...params} />}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Type / Speciality *</Typography>
            <FormControl fullWidth error={submitted && !practiceType}>
              <Select
                displayEmpty
                value={practiceType}
                onChange={(e) => setPracticeType(e.target.value)}
                renderValue={(v) => v || <Typography component="span" sx={{ color: '#9e9e9e' }}>Select your speciality</Typography>}
              >
                {MENTAL_HEALTH_SPECIALITIES.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
              {submitted && !practiceType && <FormHelperText>Type/Speciality is required</FormHelperText>}
            </FormControl>
          </Box>
        </Stack>
      </Box>
    </OnboardingLayout>
  );
};

export default Step2AboutYou;
